import { useQuery } from '@tanstack/react-query';
import { DataJob, DataJobList, DataRequestList, LegalHold, RetentionOverview } from '@cloudsign/shared';
import type { DataSubject, DataSubjectHit } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';

export const HoldList = z.array(LegalHold);
export const dpKey = ['data-protection'] as const;
export const jobsKey = ['data-protection', 'jobs'] as const;
export const holdsKey = ['data-protection', 'holds'] as const;

/** Where a job's ZIP is fetched (the server answers with a 302 to a 5-minute signed link). */
export const downloadUrl = (jobId: string): string => `/api/v1/data-protection/jobs/${jobId}/download`;

export const isRunning = (j: DataJob): boolean => j.status === 'queued' || j.status === 'running';

export function useJobs(kind: 'export' | 'erasure' | undefined, page: number) {
  return useQuery({
    queryKey: [...jobsKey, 'list', kind ?? 'all', page],
    queryFn: () => api.get('/data-protection/jobs', { query: { kind, page, pageSize: 20 }, schema: DataJobList }),
    // Keep the list fresh while anything is still working.
    refetchInterval: (q) => (q.state.data?.items.some(isRunning) ? 3000 : false),
  });
}

/** One job; polls every 3 seconds until it finishes. */
export function useJob(id: string | null) {
  return useQuery({
    queryKey: [...jobsKey, 'one', id],
    enabled: id !== null,
    queryFn: () => api.get(`/data-protection/jobs/${id}`, { schema: DataJob }),
    refetchInterval: (q) => (q.state.data && isRunning(q.state.data) ? 3000 : false),
  });
}

export function useHolds() {
  return useQuery({ queryKey: holdsKey, queryFn: () => api.get('/data-protection/holds', { schema: HoldList }) });
}

export function useRetention() {
  return useQuery({ queryKey: [...dpKey, 'retention'], queryFn: () => api.get('/data-protection/retention', { schema: RetentionOverview }) });
}

export function useRequests(status: 'open' | 'approved' | 'refused' | 'completed' | 'all') {
  return useQuery({
    queryKey: [...dpKey, 'requests', status],
    queryFn: () => api.get('/data-protection/requests', { query: { status, pageSize: 50 }, schema: DataRequestList }),
    retry: false,
  });
}

/** The person the admin has confirmed: ticked hits, plus the exact email and phone searched for. */
export function buildSubject(hits: readonly DataSubjectHit[], extra: { email?: string; phone?: string; name?: string }): DataSubject {
  const ids = (kind: string) => hits.filter((h) => h.kind === kind).map((h) => h.id);
  const uniq = (xs: string[]) => [...new Set(xs)];
  return {
    memberIds: uniq(ids('member')),
    visitIds: uniq(ids('visit')),
    preRegistrationIds: uniq(ids('preregistration')),
    emails: uniq([...ids('email'), ...(extra.email ? [extra.email.trim().toLowerCase()] : [])]),
    phones: uniq([...ids('phone'), ...(extra.phone ? [extra.phone.trim()] : [])]),
    names: extra.name ? [extra.name.trim()] : [],
  };
}

/** POST that answers 202 with a job. */
export const postJob = (path: string, body: unknown) => api.post(path, { body, schema: DataJob }) as Promise<DataJob>;

export const hitKey =(h: DataSubjectHit): string => `${h.module}:${h.kind}:${h.id}`;

export const isEmptySubject = (s: DataSubject): boolean =>
  s.memberIds.length + s.visitIds.length + s.preRegistrationIds.length + s.emails.length + s.phones.length + s.names.length === 0;
